import { useCallback, useRef } from 'react'
import { X } from 'lucide-react'
import { ModalOverlay, Button } from '@components/atoms'
import { SeatMapWithConflict } from '@components/SeatMap/SeatMap'
import { PumapungoLayout } from '@components/SeatMap/PumapungoLayout'
import { ColiseoLayout } from '@components/SeatMap/ColiseoLayout'
import { TeatroLayout } from '@components/SeatMap/TeatroLayout'
import { looksLikeColiseo } from '@components/SeatMap/coliseoZones'
import { SeatMapLegend } from '@components/SeatMap/SeatMapLegend'
import { SeatReservationTimer } from '@components/SeatMap/SeatReservationTimer'
import { Spinner } from '@components/atoms/Spinner'
import { useSeatMap } from '@modules/events/hooks/useSeatMap'
import { useSeatReservation } from '@modules/events/hooks/useSeatReservation'
import type { Locality } from '@/types'
import type { SeatData, SelectedSeat, VenueSectionData } from '@components/SeatMap/SeatMap.types'

/** Paleta de colores de sección — debe coincidir con SECTION_COLORS de PumapungoLayout. */
const LOCALITY_COLORS: Record<string, string> = {
  green:  '#22c55e',
  blue:   '#3b82f6',
  purple: '#a855f7',
  orange: '#f97316',
  pink:   '#ec4899',
}

/** Devuelve los colores hex únicos efectivamente usados por las secciones del evento. */
function extractUsedColors(sections: VenueSectionData[]): string[] {
  const seen = new Set<string>()
  for (const s of sections) {
    const key = s.color ?? 'green'
    const hex = LOCALITY_COLORS[key] ?? LOCALITY_COLORS['green']!
    seen.add(hex)
  }
  return [...seen]
}

/** Construye un mapa localityId → color usando las secciones del seat-map. */
function buildLocalityColorMap(sections: VenueSectionData[]): Map<number, string> {
  const map = new Map<number, string>()
  for (const s of sections) {
    if (s.localityId != null && s.color && !map.has(s.localityId)) {
      map.set(s.localityId, s.color)
    }
  }
  return map
}

/** Panel lateral con las localidades del evento. Muestra color asociado de las secciones. */
function LocalitiesPanel({
  localities,
  sections,
}: {
  localities: Locality[]
  sections: VenueSectionData[]
}) {
  if (localities.length === 0) return null
  const colorMap = buildLocalityColorMap(sections)

  return (
    <div className="flex flex-col gap-2 min-w-[120px]">
      <span className="text-xs text-white/50 uppercase tracking-wide font-medium">Localidades</span>
      {localities.map((loc) => {
        const colorKey = colorMap.get(Number(loc.id)) ?? 'green'
        const colorHex = LOCALITY_COLORS[colorKey] ?? LOCALITY_COLORS.green
        return (
          <div
            key={loc.id}
            className="rounded-lg px-3 py-2 flex items-center gap-2"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderLeft: `4px solid ${colorHex}`,
            }}
          >
            <span
              className="inline-block rounded-sm flex-shrink-0"
              style={{ width: 14, height: 14, backgroundColor: colorHex }}
              aria-hidden="true"
            />
            <div className="flex flex-col gap-0.5 flex-1">
              <span className="text-sm font-semibold text-white leading-tight">{loc.name}</span>
              <span className="text-xs text-secondary-120 font-medium">
                ${loc.price.toLocaleString('es-EC')}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface SeatMapModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: number
  localities: Locality[]
  /**
   * localidades-mixtas RF-2/RF-3: cuando se define, acota el modal a una
   * sola localidad (panel + selección) para eventos MIXTOS donde solo
   * algunas localidades son numeradas. Sin esta prop, comportamiento
   * actual: se muestran todas las localidades (eventos 100% numerados).
   */
  localityId?: number
  /** Called when user confirms seat selection to proceed to checkout. */
  onContinue: (params: {
    selections: Record<string, number>
    selectedSeats: SelectedSeat[]
    sessionToken: string
  }) => void
}

/**
 * Filtro puro de localidades para el panel/selección del SeatMapModal.
 * localidades-mixtas RF-2/RF-3: sin `localityId`, devuelve todas (comportamiento
 * actual para eventos 100% numerados). Con `localityId`, acota a esa única
 * localidad (evento MIXTO abriendo el mapa para una localidad numerada puntual).
 */
export function filterLocalitiesForMap<T extends { id: string | number }>(
  localities: T[],
  localityId?: number,
): T[] {
  return localityId == null ? localities : localities.filter((l) => Number(l.id) === localityId)
}

/**
 * Fix (review final) — Task 3: filtro puro de secciones para la GRILLA de
 * asientos. Sin acotar, un evento MIXTO con 2+ localidades numeradas dejaba
 * seleccionar asientos de una localidad distinta a la elegida (la grilla
 * seguía recibiendo `seatMap.sections` completas). `VenueSectionData.localityId`
 * ya es `number`, igual que la prop `localityId`, así que la comparación es
 * `===` directa. Sin `localityId`, devuelve todas (comportamiento actual).
 */
export function filterSectionsForMap<T extends { localityId?: number | null }>(
  sections: T[],
  localityId?: number,
): T[] {
  return localityId == null ? sections : sections.filter((s) => s.localityId === localityId)
}

/** Encuentra la sección que contiene el asiento dado. */
function findSectionForSeat(
  sections: VenueSectionData[],
  seatId: number,
): VenueSectionData | undefined {
  return sections.find((section) =>
    section.rows.some((row) => row.seats.some((seat) => seat.id === seatId)),
  )
}

/**
 * Full-screen modal presenting the interactive seat map for NUMBERED events.
 * Integrates useSeatMap (data) + useSeatReservation (state/API) + SeatMapWithConflict (UI).
 * localityId se deriva automáticamente de la sección del asiento seleccionado.
 */
export function SeatMapModal({
  isOpen,
  onClose,
  eventId,
  localities,
  localityId,
  onContinue,
}: SeatMapModalProps) {
  const { seatMap, loading, error } = useSeatMap(isOpen ? eventId : null)
  const visibleLocalities = filterLocalitiesForMap(localities, localityId)
  const visibleSections = seatMap ? filterSectionsForMap(seatMap.sections, localityId) : []
  const { selectedSeats, sessionToken, initialDurationSeconds, toggleSeat, clearSelection } =
    useSeatReservation()

  // Reference to the conflict-flash function exposed by SeatMapWithConflict
  const flashConflictRef = useRef<((seatId: number) => void) | null>(null)

  // Límite de boletos configurado en el backend (campo "Cantidad por cada venta")
  const maxSeats = seatMap?.maxSeatsPerPurchase ?? 1

  const handleSeatClick = useCallback(
    async (seat: SeatData) => {
      const isAlreadySelected = selectedSeats.some((s) => s.seatId === seat.id)
      // Block new selections when limit is reached (deselect always allowed)
      if (!isAlreadySelected && selectedSeats.length >= maxSeats) return
      await toggleSeat(eventId, seat, (conflictId) => {
        flashConflictRef.current?.(conflictId)
      })
    },
    [eventId, toggleSeat, selectedSeats, maxSeats],
  )

  const handleExpire = useCallback(() => {
    clearSelection()
  }, [clearSelection])

  const handleContinue = () => {
    if (selectedSeats.length === 0 || !sessionToken) return

    // Agrupar asientos por localityId (derivado de la sección) — R1, D2
    const selectionsMap: Record<string, number> = {}
    for (const seat of selectedSeats) {
      const section = seatMap ? findSectionForSeat(seatMap.sections, seat.seatId) : undefined
      const localityId = String(section?.localityId ?? '')
      selectionsMap[localityId] = (selectionsMap[localityId] ?? 0) + 1
    }

    onContinue({
      selections: selectionsMap,
      selectedSeats,
      sessionToken,
    })
  }

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose}>
      <div
        className="flex flex-col rounded-2xl bg-primary-110 px-4 sm:px-8 py-6 w-full max-w-[1350px] overflow-y-auto"
        style={{ maxHeight: '90dvh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <span className="font-semibold text-white" style={{ fontSize: 20 }}>
            Seleccionar asiento
          </span>
          <button
            className="flex cursor-pointer items-center justify-center border-none bg-transparent text-white"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Spinner />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="rounded-lg bg-red-900/50 px-4 py-3 text-red-300 text-sm mb-4">
            {error}
          </div>
        )}

        {/* Seat map */}
        {!loading && seatMap && (
          <>
            {/* Top: localidades (izquierda) + imagen (derecha) */}
            <div className="mb-4 flex flex-col sm:flex-row gap-4 items-start">
              <LocalitiesPanel localities={visibleLocalities} sections={visibleSections} />
              {seatMap.venueImageUrl && (
                <div className="flex-1 min-w-0 overflow-hidden rounded-xl">
                  <img
                    src={seatMap.venueImageUrl}
                    alt="Plano del recinto"
                    className="w-full object-contain max-h-[260px]"
                  />
                </div>
              )}
            </div>

            {/* Mapa — ancho completo sin columna lateral */}
            <div className="mb-4 overflow-x-auto [-webkit-overflow-scrolling:touch]">
              {seatMap.venueId === 1 ? (
                <PumapungoLayout
                  sections={visibleSections}
                  selectedSeats={selectedSeats}
                  onSeatClick={handleSeatClick}
                  onConflictRef={(fn) => { flashConflictRef.current = fn }}
                />
              ) : looksLikeColiseo(seatMap.sections) ? (
                <ColiseoLayout
                  sections={visibleSections}
                  selectedSeats={selectedSeats}
                  onSeatClick={handleSeatClick}
                  onConflictRef={(fn) => { flashConflictRef.current = fn }}
                />
              ) : seatMap.venueName?.toLowerCase().includes('casa de la cultura') ? (
                <TeatroLayout
                  sections={visibleSections}
                  selectedSeats={selectedSeats}
                  onSeatClick={handleSeatClick}
                  onConflictRef={(fn) => { flashConflictRef.current = fn }}
                  venueName={seatMap.venueName}
                />
              ) : (
                <SeatMapWithConflict
                  sections={visibleSections}
                  selectedSeats={selectedSeats}
                  onSeatClick={handleSeatClick}
                  onConflictRef={(fn) => { flashConflictRef.current = fn }}
                />
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-2">
              <SeatMapLegend availableColors={extractUsedColors(seatMap.sections)} />
              <SeatReservationTimer
                durationSeconds={initialDurationSeconds}
                onExpire={handleExpire}
              />
            </div>

            {/* Limit notice */}
            {selectedSeats.length >= maxSeats && (
              <div className="mt-3 rounded-lg bg-yellow-500/20 px-4 py-2 text-yellow-300 text-sm">
                Límite alcanzado: puedes seleccionar máximo {maxSeats} asiento{maxSeats !== 1 ? 's' : ''}.
              </div>
            )}

            {/* Selected seats info */}
            {selectedSeats.length > 0 && (
              <div className="mt-3 rounded-lg bg-white/10 px-4 py-3 text-white text-sm flex flex-col gap-1">
                <span className="text-white/60 text-xs uppercase tracking-wide">
                  {maxSeats > 1 ? 'Asientos seleccionados' : 'Asiento seleccionado'}
                </span>
                {selectedSeats.map((s) => {
                  const section = seatMap ? findSectionForSeat(seatMap.sections, s.seatId) : undefined
                  return (
                    <div key={s.seatId} className="flex items-center justify-between gap-4">
                      <span className="font-semibold">{s.seatLabel}</span>
                      <div className="flex items-center gap-3 text-xs">
                        {section?.localityName && (
                          <span className="rounded-full bg-secondary-120/20 text-secondary-120 px-2 py-0.5 font-medium">
                            {section.localityName}
                          </span>
                        )}
                        {section?.localityPrice != null && (
                          <span className="text-white/80 font-medium">
                            ${section.localityPrice.toLocaleString('es-EC')}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {/* Footer action */}
        <div className="mt-6 flex justify-center sticky bottom-0 bg-primary-110 pt-4">
          <Button
            className="w-full max-w-[340px] h-[50px] bg-secondary-120 text-primary-110 hover:bg-secondary-120 hover:opacity-90 disabled:opacity-40"
            disabled={selectedSeats.length === 0 || !sessionToken}
            onClick={handleContinue}
          >
            Continuar con pago
          </Button>
        </div>
      </div>
    </ModalOverlay>
  )
}
